// Shared building blocks for the analytics dashboard: colours, section
// frames, KPI cards, compact dropdowns and the sortable data table.

import React, { useMemo, useState } from 'react';
import {
  Autocomplete, Box, Card, Checkbox, IconButton, InputBase, ListItemText, Menu, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Tooltip, Typography,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';

/** Dark and light colours. Accents are a step darker in light mode so
 *  text and thin marks keep their contrast on white. */
export const PALETTES = {
  dark: {
    bg: '#0f1419', card: '#1a2332', cardAlt: '#151d2a', border: '#2a3444', text: '#ffffff', muted: '#8b95a5',
    hover: '#212b3b', faint: '#ffffff10', scheme: 'dark',
    green: '#00D084', orange: '#FF6B4A', blue: '#1890FF', cyan: '#00B8D4', yellow: '#FAAD14', purple: '#9C6ADE',
  },
  light: {
    bg: '#F3F5F8', card: '#FFFFFF', cardAlt: '#F7F9FB', border: '#D5DAE1', text: '#111827', muted: '#4B5563',
    hover: '#EEF2F6', faint: '#0000000d', scheme: 'light',
    green: '#007A50', orange: '#C2410C', blue: '#1D6FD8', cyan: '#0E7490', yellow: '#B45309', purple: '#7C3AED',
  },
} as const;
export type PaletteMode = keyof typeof PALETTES;

/** The current colours. setPalette switches them; AnalyticsPage remounts the
 *  pages when it does, so everything re-reads C. */
export const C: { -readonly [K in keyof typeof PALETTES.dark]: string } = { ...PALETTES.dark };
export const setPalette = (mode: PaletteMode) => { Object.assign(C, PALETTES[mode]); };

export const fmt = (n: number) => Math.round(n).toLocaleString();
export const fmtShort = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(1)}K` : fmt(n);
export const pct = (n: number, d: number) => (d > 0 ? (n / d) * 100 : 0);

// ---- section frame ----------------------------------------------------------

export const Section: React.FC<{
  id: string; icon?: React.ReactNode; title: string; subtitle?: string; accent?: string;
  actions?: React.ReactNode; children: React.ReactNode;
}> = ({ id, icon, title, subtitle, accent = C.green, actions, children }) => (
  <Box id={id} component="section" sx={{ mb: 4 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
      {icon && (
        <Box sx={{
          width: 36, height: 36, borderRadius: 1, backgroundColor: `${accent}22`, color: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>{icon}</Box>
      )}
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, lineHeight: 1.2 }}>{title}</Typography>
        {subtitle && <Typography sx={{ fontSize: '0.72rem', color: C.muted }}>{subtitle}</Typography>}
      </Box>
      {actions}
    </Box>
    {children}
  </Box>
);

export const Panel: React.FC<{ title?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode; sx?: object }> = ({ title, actions, children, sx }) => (
  <Card sx={{ backgroundColor: C.card, border: `1px solid ${C.border}`, borderRadius: 1, height: '100%', display: 'flex', flexDirection: 'column', ...sx }}>
    {(title || actions) && (
      <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 1, minHeight: 42 }}>
        <Typography component="div" sx={{ fontSize: '0.8rem', fontWeight: 600, flex: 1 }}>{title}</Typography>
        {actions}
      </Box>
    )}
    <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
  </Card>
);

// ---- KPI card ---------------------------------------------------------------

export const Kpi: React.FC<{
  label: string; value: number | string; sub?: string; color?: string; onClick?: () => void; active?: boolean;
}> = ({ label, value, sub, color = C.text, onClick, active }) => (
  <Card onClick={onClick} sx={{
    backgroundColor: C.card, border: `1px solid ${active ? color : C.border}`, borderRadius: 1, p: 1.5, height: '100%',
    cursor: onClick ? 'pointer' : 'default', transition: 'border-color .15s, transform .15s',
    '&:hover': onClick ? { borderColor: color, transform: 'translateY(-1px)' } : undefined,
  }}>
    <Typography sx={{ fontSize: '0.66rem', color: C.muted, fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.55rem', fontWeight: 700, color, lineHeight: 1.15, mt: 0.5 }}>
      {typeof value === 'number' ? fmt(value) : value}
    </Typography>
    {sub && <Typography sx={{ fontSize: '0.68rem', color: C.muted, mt: 0.25 }}>{sub}</Typography>}
  </Card>
);

// ---- dropdown ---------------------------------------------------------------

export interface Opt { value: string; label: string }

const fieldLabel = () => ({ fontSize: '0.62rem', color: C.muted, fontWeight: 700, letterSpacing: 0.5, mb: 0.4, textTransform: 'uppercase' } as const);

const inputSx = () => ({
  '& .MuiOutlinedInput-root': {
    fontSize: '0.8rem', backgroundColor: C.bg, minHeight: 40, py: '0 !important',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.muted },
    '&.Mui-focused fieldset': { borderColor: C.green },
  },
  '& .MuiSvgIcon-root': { color: C.muted },
  '& .Mui-disabled': { opacity: 0.55 },
});

/**
 * A searchable dropdown: click to open, type to filter. '' means "all".
 * Long lists (wards, polling centres) stay quick to use.
 */
export const Dropdown: React.FC<{
  label: string; value: string; options: (string | Opt)[]; onChange: (v: string) => void;
  placeholder?: string; disabled?: boolean; minWidth?: number; clearable?: boolean;
  /** Show "N options · type to search" under long lists. */
  hint?: boolean;
}> = ({ label, value, options, onChange, placeholder = 'All', disabled, minWidth = 170, clearable = true, hint = true }) => {
  const opts: Opt[] = options.map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
  const selected = opts.find((o) => o.value === value) || null;
  return (
    <Box sx={{ minWidth, flex: '1 1 auto', maxWidth: 280 }}>
      <Typography sx={fieldLabel()}>{label}</Typography>
      <Autocomplete
        size="small" options={opts} value={selected} disabled={disabled} disableClearable={!clearable}
        onChange={(_, o) => onChange(o ? o.value : '')}
        getOptionLabel={(o) => o.label} isOptionEqualToValue={(a, b) => a.value === b.value}
        noOptionsText="No matches" autoHighlight openOnFocus
        slotProps={{ paper: { sx: { backgroundColor: C.card, color: C.text, border: `1px solid ${C.border}`, fontSize: '0.8rem' } } }}
        renderOption={(props, o) => (
          <li {...props} key={o.value} style={{ fontSize: '0.8rem' }}>{o.label}</li>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} sx={inputSx()}
            helperText={hint && !disabled && opts.length > 8 ? `${opts.length} options · type to search` : undefined}
            FormHelperTextProps={{ sx: { fontSize: '0.6rem', color: C.muted, mx: 0, mt: 0.25 } }} />
        )}
      />
    </Box>
  );
};

export const FilterBar: React.FC<{ children: React.ReactNode; onClear?: () => void }> = ({ children, onClear }) => (
  <Box sx={{
    display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start', p: 1.5, mb: 2,
    backgroundColor: C.cardAlt, border: `1px solid ${C.border}`, borderRadius: 1,
  }}>
    {children}
    {onClear && (
      <Typography onClick={onClear} sx={{ fontSize: '0.72rem', color: C.cyan, cursor: 'pointer', alignSelf: 'center', mt: 1.5, '&:hover': { textDecoration: 'underline' } }}>
        Clear filters
      </Typography>
    )}
  </Box>
);

// ---- date filter ------------------------------------------------------------

/** A from/to day range plus the preset it came from ('' = custom). */
export interface DateFilterValue { preset: string; from: string; to: string }

const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/** Preset periods, measured back from the last day with data. */
export function presetRange(preset: string, first: string, last: string): [string, string] {
  const clamp = (d: string) => (d < first ? first : d);
  switch (preset) {
    case 'today': return [last, last];
    case 'yesterday': return [clamp(addDays(last, -1)), clamp(addDays(last, -1))];
    case '7d': return [clamp(addDays(last, -6)), last];
    case '30d': return [clamp(addDays(last, -29)), last];
    case 'month': return [clamp(`${last.slice(0, 7)}-01`), last];
    case 'lastmonth': {
      const firstOfThis = `${last.slice(0, 7)}-01`;
      const lastOfPrev = addDays(firstOfThis, -1);
      return [clamp(`${lastOfPrev.slice(0, 7)}-01`), clamp(lastOfPrev)];
    }
    case 'year': return [clamp(`${last.slice(0, 4)}-01-01`), last];
    default: return [first, last];
  }
}

export const DATE_PRESETS: Opt[] = [
  { value: 'all', label: 'All dates' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'month', label: 'This month' },
  { value: 'lastmonth', label: 'Last month' },
  { value: 'year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

export const allDates = (first: string, last: string): DateFilterValue => ({ preset: 'all', from: first, to: last });

/** Period dropdown + from/to day pickers. Picking a preset fills the days;
 *  editing a day switches to "Custom range". */
export const DateFilter: React.FC<{
  label: string; value: DateFilterValue; onChange: (v: DateFilterValue) => void; first: string; last: string;
}> = ({ label, value, onChange, first, last }) => {
  const dateSx = {
    fontSize: '0.8rem', color: C.text, backgroundColor: C.bg, border: `1px solid ${C.border}`, borderRadius: 1,
    px: 1, height: 40, width: 150, colorScheme: C.scheme,
  };
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <Dropdown label={label} value={value.preset} options={DATE_PRESETS} clearable={false} minWidth={160}
        onChange={(p) => {
          if (p === 'custom' || !p) { onChange({ ...value, preset: 'custom' }); return; }
          const [from, to] = presetRange(p, first, last);
          onChange({ preset: p, from, to });
        }} />
      {value.preset === 'custom' && (<>
      <Box>
        <Typography sx={fieldLabel()}>From</Typography>
        <InputBase type="date" value={value.from} inputProps={{ min: first, max: value.to }} sx={dateSx}
          onChange={(e) => e.target.value && onChange({ preset: 'custom', from: e.target.value, to: value.to < e.target.value ? e.target.value : value.to })} />
      </Box>
      <Box>
        <Typography sx={fieldLabel()}>To</Typography>
        <InputBase type="date" value={value.to} inputProps={{ min: value.from, max: last }} sx={dateSx}
          onChange={(e) => e.target.value && onChange({ preset: 'custom', from: value.from > e.target.value ? e.target.value : value.from, to: e.target.value })} />
      </Box>
      </>)}
    </Box>
  );
};

// ---- data table ---------------------------------------------------------------

export interface Column<R> {
  key: string;
  label: string;
  value: (r: R) => number | string;
  /** 'num' right-aligns and formats; 'pct' adds an inline coverage bar
   *  (higher = better); 'percent' is a plain one-decimal percentage. */
  kind?: 'text' | 'num' | 'pct' | 'percent';
  color?: string;
  /** Hidden until switched on from the column picker. */
  optional?: boolean;
  /** Value for the totals row (numbers are summed by default; pct must say). */
  total?: (rows: R[]) => number | string;
}

export function DataTable<R>({ rows, columns, onRowClick, maxHeight = 420, searchPlaceholder = 'Search…', fileName = 'table', emptyText = 'No rows', rowKey }: {
  rows: R[]; columns: Column<R>[]; onRowClick?: (r: R) => void; maxHeight?: number;
  searchPlaceholder?: string; fileName?: string; emptyText?: string; rowKey: (r: R) => string;
}) {
  const [sortKey, setSortKey] = useState<string>(columns.find((c) => c.kind === 'num')?.key || columns[0].key);
  const [desc, setDesc] = useState(true);
  const [q, setQ] = useState('');
  const [shown, setShown] = useState<Record<string, boolean>>(() => Object.fromEntries(columns.map((c) => [c.key, !c.optional])));
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  const visible = columns.filter((c) => shown[c.key]);
  const textCol = columns.find((c) => (c.kind ?? 'text') === 'text');

  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sortKey) || columns[0];
    const f = q.trim().toLowerCase();
    const list = f && textCol ? rows.filter((r) => String(textCol.value(r)).toLowerCase().includes(f)) : rows.slice();
    list.sort((a, b) => {
      const x = col.value(a), y = col.value(b);
      const cmp = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y));
      return desc ? -cmp : cmp;
    });
    return list;
  }, [rows, columns, sortKey, desc, q, textCol]);

  const totals = (c: Column<R>) => {
    if (c.total) return c.total(sorted);
    if (c.kind === 'num') return sorted.reduce((s, r) => s + (Number(c.value(r)) || 0), 0);
    return '';
  };

  const exportCsv = () => {
    const esc = (v: unknown) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [visible.map((c) => esc(c.label)).join(','), ...sorted.map((r) => visible.map((c) => esc(c.value(r))).join(','))];
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([lines.join('\n')], { type: 'text/csv' }));
    a.download = `${fileName}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const cell = (c: Column<R>, v: number | string) => {
    if (c.kind === 'pct' && typeof v === 'number') {
      const color = v >= 80 ? C.green : v >= 60 ? C.yellow : C.orange;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
          <Box sx={{ width: 56, height: 6, borderRadius: 3, backgroundColor: C.border, overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(100, v)}%`, height: '100%', backgroundColor: color }} />
          </Box>
          <span style={{ color, fontWeight: 700, minWidth: 42, textAlign: 'right' }}>{v.toFixed(1)}%</span>
        </Box>
      );
    }
    if (c.kind === 'percent' && typeof v === 'number') return `${v.toFixed(1)}%`;
    return typeof v === 'number' ? fmt(v) : v;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.75, borderBottom: `1px solid ${C.border}` }}>
        <SearchIcon sx={{ fontSize: 16, color: C.muted }} />
        <InputBase value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchPlaceholder}
          sx={{ flex: 1, fontSize: '0.78rem', color: C.text }} />
        <Typography sx={{ fontSize: '0.68rem', color: C.muted, mr: 0.5 }}>{sorted.length} rows</Typography>
        <Tooltip title="Columns"><IconButton size="small" onClick={(e) => setMenu(e.currentTarget)}><ViewColumnIcon sx={{ fontSize: 18, color: C.muted }} /></IconButton></Tooltip>
        <Tooltip title="Download CSV"><IconButton size="small" onClick={exportCsv}><FileDownloadIcon sx={{ fontSize: 18, color: C.muted }} /></IconButton></Tooltip>
        <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)} PaperProps={{ sx: { backgroundColor: C.card, color: C.text } }}>
          {columns.filter((c) => c !== textCol).map((c) => (
            <MenuItem key={c.key} dense onClick={() => setShown((s) => ({ ...s, [c.key]: !s[c.key] }))}>
              <Checkbox size="small" checked={!!shown[c.key]} sx={{ p: 0.5, mr: 1, color: C.muted }} />
              <ListItemText primaryTypographyProps={{ fontSize: '0.8rem' }}>{c.label}</ListItemText>
            </MenuItem>
          ))}
        </Menu>
      </Box>
      <TableContainer sx={{ maxHeight, flex: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {visible.map((c) => (
                <TableCell key={c.key} align={c.kind && c.kind !== 'text' ? 'right' : 'left'}
                  sx={{ backgroundColor: C.cardAlt, color: C.muted, fontSize: '0.66rem', fontWeight: 700, letterSpacing: 0.4, borderBottom: `1px solid ${C.border}`, py: 0.75, whiteSpace: 'nowrap' }}>
                  <TableSortLabel active={sortKey === c.key} direction={sortKey === c.key && !desc ? 'asc' : 'desc'}
                    onClick={() => { if (sortKey === c.key) setDesc(!desc); else { setSortKey(c.key); setDesc(c.kind !== 'text'); } }}
                    sx={{ color: `${C.muted} !important`, '& .MuiTableSortLabel-icon': { color: `${C.muted} !important` } }}>
                    {c.label.toUpperCase()}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && (
              <TableRow><TableCell colSpan={visible.length} sx={{ color: C.muted, textAlign: 'center', py: 3, border: 'none' }}>{emptyText}</TableCell></TableRow>
            )}
            {sorted.map((r) => (
              <TableRow key={rowKey(r)} hover onClick={onRowClick ? () => onRowClick(r) : undefined}
                sx={{ cursor: onRowClick ? 'pointer' : 'default', '&:hover': { backgroundColor: `${C.hover} !important` } }}>
                {visible.map((c) => (
                  <TableCell key={c.key} align={c.kind && c.kind !== 'text' ? 'right' : 'left'}
                    sx={{ color: c.color || C.text, fontSize: '0.78rem', borderBottom: `1px solid ${C.border}`, py: 0.6, whiteSpace: 'nowrap', fontWeight: c.kind === 'text' || !c.kind ? 600 : 500 }}>
                    {cell(c, c.value(r))}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
          {sorted.length > 1 && visible.some((c) => c.kind && c.kind !== 'text') && (
            <TableBody sx={{ position: 'sticky', bottom: 0 }}>
              <TableRow>
                {visible.map((c, i) => (
                  <TableCell key={c.key} align={c.kind && c.kind !== 'text' ? 'right' : 'left'}
                    sx={{ backgroundColor: C.cardAlt, color: c.color || C.text, fontSize: '0.78rem', fontWeight: 700, borderTop: `1px solid ${C.border}`, py: 0.75 }}>
                    {i === 0 ? 'TOTAL' : cell(c, totals(c))}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      {onRowClick && <Typography sx={{ fontSize: '0.65rem', color: C.muted, px: 1, py: 0.5 }}>Click a row to drill down</Typography>}
    </Box>
  );
}

/** Pill toggle group for small choices (Chart / Table, Month / Quarter…). */
export const Toggle: React.FC<{ value: string; options: Opt[]; onChange: (v: string) => void }> = ({ value, options, onChange }) => (
  <Box sx={{ display: 'flex', backgroundColor: C.bg, border: `1px solid ${C.border}`, borderRadius: 5, p: 0.25 }}>
    {options.map((o) => (
      <Box key={o.value} onClick={() => onChange(o.value)} sx={{
        px: 1.25, py: 0.35, borderRadius: 5, fontSize: '0.7rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
        color: value === o.value ? C.bg : C.muted, backgroundColor: value === o.value ? C.green : 'transparent',
      }}>{o.label}</Box>
    ))}
  </Box>
);
